import { Select as AntdSelect } from 'antd'
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Input,
  Button,
  Select,
  ContentBody,
  Row,
  Col,
  ContentHeader,
  DatePicker,
  Card,
  Modal,
  AddressForm,
  AddressCard,
  ContactForm,
  ContactCard,
  Empty,
  TextArea
} from '../../../core/components'
import { useHistory } from 'react-router-dom'
import { ChangeEvent, useEffect, useState } from 'react'
import { AddressWithId, ContactWithId } from '../../../entities'

import { v4 } from 'uuid'
import { handleChange } from '../../../utils/handleChange'
import { CustomerToSave } from '../../../entities/customerToSave'
import useBusinessUnits from '../../../services/queries/useBusinesUnits'
import { colors } from '../../../core/theme'
import useCustomerCreate from '../../../services/mutations/useCutomerCreate'
import { formatCnpjCpf } from '../../../core/utils'
import { phoneFormat } from '../../../utils/format/phoneFormat'
import { mobileFormat } from '../../../utils/format/mobileFormat'

const { Option } = AntdSelect

const contactDefaultValues = {
  id: '',
  name: '',
  role: '',
  phoneNumber: '',
  cellNumber: '',
  email: '',
  observation: '',
  gender: ''
}

const addressDefaultValues = {
  id: '',
  type: '',
  address: '',
  number: '',
  district: '',
  complement: '',
  zipCode: '',
  city: '',
  state: ''
}

const customerDefaultValue = {
  name: '',
  nickname: '',
  employerNumber: '',
  registrationNumber: '',
  businessUnitId: '001',
  customerTypeId: '',
  birthday: '',
  addresses: [],
  contacts: []
}

const legalEntities: any = [
  {
    key: 0,
    label: 'JURÍDICA'
  },
  {
    key: 1,
    label: 'FÍSICA'
  }
]

export const CreateCustomerPage = () => {
  const { goBack } = useHistory()
  const [addressForm, setAddressForm] =
    useState<AddressWithId>(addressDefaultValues)
  const [contactForm, setContactForm] =
    useState<ContactWithId>(contactDefaultValues)
  const [formValues, setFormValues] =
    useState<CustomerToSave>(customerDefaultValue)
  const [isAddressModalVisible, setIsAddressModalVisible] =
    useState<boolean>(false)
  const [isContactModalVisible, setIsContactModalVisible] =
    useState<boolean>(false)
  const [contacts, setContacts] = useState<ContactWithId[]>([])
  const [addresses, setAddresses] = useState<AddressWithId[]>([])
  const [isAddressEditing, setIsAddressEditing] = useState<boolean>(false)
  const [isContactEditing, setIsContactEditing] = useState<boolean>(false)
  const [errors, setErrors] = useState<string[]>([])
  const [formState, setFormState] = useState({ isError: false })
  const [legalEntity, setLegalEntity] = useState(0)

  const { data, isLoading } = useBusinessUnits({ page: 1, limit: 99 })
  const { mutate, isLoading: isSubmiting, isSuccess } = useCustomerCreate()

  const isError = () => {
    const errors = []
    if (!addresses.length) errors.push('ENDEREÇO É OBRIGATÓRIO!')

    setErrors(errors)
    return !!errors.length
  }

  const showContactModal = () => {
    setIsContactModalVisible(true)
  }

  const handleContactOk = () => {
    setIsContactModalVisible(false)
    if (!isContactEditing) {
      setContacts((prev) => prev.concat({ ...contactForm, id: v4() }))
    } else {
      setContacts(
        contacts.map((item) =>
          item.id === contactForm.id ? contactForm : item
        )
      )
    }
    setContactForm(contactDefaultValues)
  }

  const handleContactCancel = () => {
    setIsContactModalVisible(false)
    setContactForm(contactDefaultValues)
  }

  const handleContactRemove = (id: string) => {
    setContacts((prev) => prev.filter((item) => item.id !== id))
  }

  const handleContactEdit = (id: string) => {
    setIsContactEditing(true)
    setContactForm(
      contacts.find((item) => item.id === id) || contactDefaultValues
    )
    showContactModal()
  }

  const showAddressModal = () => {
    setIsAddressModalVisible(true)
  }

  const handleAddressOk = () => {
    setIsAddressModalVisible(false)
    if (!isAddressEditing) {
      let addressType = 'PRINCIPAL/INSTALACAO'
      if (addresses.length > 0) addressType = 'INSTALACAO'
      if (addresses.length === 1) {
        setAddresses((prev) =>
          [{ ...prev[0], type: 'PRINCIPAL' }].concat({
            ...addressForm,
            type: addressForm.type || addressType,
            id: v4()
          })
        )
      } else {
        setAddresses((prev) =>
          prev.concat({
            ...addressForm,
            type: addressForm.type || addressType,
            id: v4()
          })
        )
      }
    } else {
      setAddresses(
        addresses.map((item) =>
          item.id === addressForm.id ? addressForm : item
        )
      )
    }
    setAddressForm(addressDefaultValues)
  }

  const handleAddressCancel = () => {
    setIsAddressModalVisible(false)
    setAddressForm(addressDefaultValues)
  }

  const handleAddressRemove = (id: string) => {
    setAddresses((prev) => {
      let newAddresses = prev.filter((item) => item.id !== id)

      if (newAddresses.length === 1)
        newAddresses = [{ ...newAddresses[0], type: 'PRINCIPAL/INSTALACAO' }]

      return newAddresses
    })
  }

  const handleAddresseEdit = (id: string) => {
    setIsAddressEditing(true)
    setAddressForm(
      addresses.find((item) => item.id === id) || addressDefaultValues
    )
    showAddressModal()
  }

  const handleMainAddress = (id: string) => {
    setAddresses((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, type: 'PRINCIPAL' }
          : { ...item, type: 'INSTALACAO' }
      )
    )
  }

  const handleSubmit = () => {
    if (!isError()) {
      let newAddresses = addresses
      if (addresses.length === 1) {
        newAddresses = [
          { ...addresses[0], type: 'PRINCIPAL' },
          { ...addresses[0], type: 'INSTALACAO' }
        ]
      }

      newAddresses = newAddresses.map((item) => ({
        ...item,
        zipCode: Number(String(item.zipCode).replace(/\D/g, ''))
      }))

      const newContacts = contacts.map((item) => ({
        ...item,
        cellNumber: item.cellNumber?.replace(/\D/g, ''),
        phoneNumber: item.phoneNumber?.replace(/\D/g, '')
      }))

      const cnpjCpf = formValues.employerNumber.replace(/\D/g, '')

      const submitValues = {
        ...formValues,
        employerNumber: cnpjCpf,
        addresses: newAddresses,
        contacts: newContacts
      }
      mutate(submitValues)
      if (isSuccess) goBack()
    }
  }

  const handleReset = () => {
    setFormValues(customerDefaultValue)
    setAddresses([])
    setContacts([])
  }

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    setContacts((prev: any) => [
      {
        ...prev[0],
        phoneNumber: phoneFormat(e.target.value)
      }
    ])
  }

  const handleMobileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setContacts((prev: any) => [
      {
        ...prev[0],
        cellNumber: mobileFormat(e.target.value)
      }
    ])
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setContacts((prev: any) => [
      {
        ...prev[0],
        email: e.target.value
      }
    ])
  }

  const handleObservationChange = (e: ChangeEvent<HTMLInputElement>) => {
    setContacts((prev: any) => [
      {
        ...prev[0],
        observation: e.target.value && e.target.value.toUpperCase()
      }
    ])
  }

  useEffect(() => {
    isError()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses])

  useEffect(() => {
    setFormValues((prev: any) => ({
      ...prev,
      employerNumber: formatCnpjCpf(String(formValues.employerNumber))
    }))
  }, [formValues.employerNumber])

  return (
    <div>
      <ContentHeader>
        <Button type="text" onClick={goBack}>
          <ArrowLeftOutlined />
        </Button>
        Novo Cliente
      </ContentHeader>
      <ContentBody>
        <h2>Informações Gerais</h2>
        <Card style={{ marginBottom: 20 }}>
          <Col>
            <Select
              label="Tipo Pessoa"
              value={legalEntity}
              onSelect={(customerTypeId) => setLegalEntity(customerTypeId)}
            >
              {legalEntities.map((legalEntity: any) => (
                <Option key={legalEntity.key} value={legalEntity.key}>
                  {legalEntity.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Input
              label="Nome do Cliente"
              value={formValues.name}
              onChange={(e) => handleChange(e, 'name', setFormValues)}
            />
          </Col>
          <Col>
            <Input
              label="Fantasia/Apelido"
              value={formValues.nickname}
              onChange={(e) => handleChange(e, 'nickname', setFormValues)}
            />
          </Col>
          <Col>
            <Input
              label="CNPJ/CPF"
              value={formValues.employerNumber}
              onChange={(e) => handleChange(e, 'employerNumber', setFormValues)}
            />
          </Col>
          <Col>
            <Input
              label="Inscrição Estadual"
              value={formValues.registrationNumber}
              onChange={(e) =>
                handleChange(e, 'registrationNumber', setFormValues)
              }
            />
          </Col>
          <Col>
            <Select
              label="Unidade de Negócio"
              value={formValues.businessUnitId}
              showSearch
              loading={isLoading}
              filterOption={(input, option) =>
                option?.children.toUpperCase().indexOf(input.toUpperCase()) >= 0
              }
              disabled
              onSelect={(businessUnitId) =>
                setFormValues((prev) => ({ ...prev, businessUnitId }))
              }
            >
              {data?.data?.map((bu) => (
                <Option key={bu.id} value={bu.id}>
                  {`${bu.id} - ${bu.name}`}
                </Option>
              ))}
            </Select>
          </Col>
          {legalEntity === 1 && (
            <>
              <Col>
                <Input
                  label="Telefone"
                  value={contacts[0]?.phoneNumber}
                  onChange={handlePhoneChange}
                />
              </Col>
              <Col>
                <Input
                  label="Celular"
                  value={contacts[0]?.cellNumber}
                  onChange={handleMobileChange}
                />
              </Col>
              <Col>
                <Input
                  label="E-mail"
                  type="email"
                  value={contacts[0]?.email}
                  onChange={handleEmailChange}
                  // error={errors.email}
                />
              </Col>
              <Col>
                <TextArea
                  label="Observação"
                  rows={4}
                  value={contacts[0]?.observation}
                  onChange={handleObservationChange}
                />
              </Col>
            </>
          )}
        </Card>
        {legalEntity === 0 && (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <h2 style={{ paddingRight: 10 }}>Contatos</h2>
              <Button
                icon={<PlusOutlined style={{ color: colors.primary }} />}
                type="link"
                style={{ color: colors.text, padding: 0, paddingBottom: 5 }}
                onClick={() => {
                  showContactModal()
                  setIsContactEditing(false)
                }}
              >
                Adicionar
              </Button>
            </div>
            <Card
              style={{
                background: colors.placeholder,
                marginBottom: 20
              }}
            >
              <Modal
                title={isContactEditing ? 'Atualizar Contato' : 'Novo Contato'}
                visible={isContactModalVisible}
                onCancel={handleContactCancel}
                footer={[
                  <Button key="back" onClick={handleContactCancel}>
                    Cancelar
                  </Button>,
                  <Button key="submit" type="primary" onClick={handleContactOk}>
                    {isContactEditing ? 'Atualizar' : 'Adicionar'}
                  </Button>
                ]}
              >
                <ContactForm value={contactForm} setValue={setContactForm} />
              </Modal>
              <Row gutter={[20, 20]}>
                {contacts.length > 0 ? (
                  contacts.map((contact) => (
                    <Col key={contact.id} xxl={6} xl={8} sm={12}>
                      <ContactCard
                        edit={handleContactEdit}
                        remove={handleContactRemove}
                        state={contact}
                      />
                    </Col>
                  ))
                ) : (
                  <Empty
                    style={{ width: '100%' }}
                    description="Nenhum contato adicionado"
                  />
                )}
              </Row>
            </Card>
          </>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
        >
          <h2 style={{ paddingRight: 10 }}>Endereços</h2>
          <Button
            icon={<PlusOutlined style={{ color: colors.primary }} />}
            type="link"
            style={{ color: colors.text, padding: 0, paddingBottom: 5 }}
            onClick={() => {
              showAddressModal()
              setIsAddressEditing(false)
            }}
          >
            Adicionar
          </Button>
        </div>
        <Card
          style={{
            background: colors.placeholder,
            marginBottom: 20
          }}
        >
          <Col>
            <Modal
              title={isAddressEditing ? 'Atualizar Endereço' : 'Novo Endereço'}
              visible={isAddressModalVisible}
              onCancel={handleAddressCancel}
              footer={[
                <Button key="back" onClick={handleAddressCancel}>
                  Cancelar
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  onClick={handleAddressOk}
                  disabled={formState.isError}
                >
                  {isAddressEditing ? 'Atualizar' : 'Adicionar'}
                </Button>
              ]}
            >
              <AddressForm
                value={addressForm}
                setValue={setAddressForm}
                setFormState={setFormState}
              />
            </Modal>
            <Row gutter={[20, 20]}>
              {addresses.length > 0 ? (
                addresses.map((address) => {
                  return (
                    <Col key={address.id} xxl={6} xl={8} sm={12}>
                      <AddressCard
                        state={address}
                        remove={handleAddressRemove}
                        edit={handleAddresseEdit}
                        setMainAddress={handleMainAddress}
                      />
                    </Col>
                  )
                })
              ) : (
                <Empty
                  style={{ width: '100%' }}
                  description="Nenhum endereço adicionado"
                />
              )}
            </Row>
          </Col>
        </Card>
        <Row gutter={24} style={{ marginTop: 20 }}>
          <Col span={24}>
            <span style={{ color: colors.error }}>{errors}</span>
          </Col>
          <Col>
            <Button
              onClick={() => {
                console.log('teste')
                handleSubmit()
              }}
              type="primary"
              style={{ width: 150 }}
              disabled={isSubmiting}
              loading={isSubmiting}
            >
              Cadastrar
            </Button>
          </Col>
          <Col>
            <Button type="default" onClick={handleReset}>
              Reiniciar
            </Button>
          </Col>
        </Row>
      </ContentBody>
    </div>
  )
}
